import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import LazyImage from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoriesIcon from "@SupplyLand/Icons/CategoriesIcon";
import CleaningAndJanitorialIcon from "@SupplyLand/Icons/CleaningAndJanitorialIcon";
import FacilityOperationsIcon from "@SupplyLand/Icons/FacilityOperationsIcon";
import SafetyAndSecurityIcon from "@SupplyLand/Icons/SafetyAndSecurityIcon";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
});

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

export interface CategoryNameStyles {
    titleText?: TypographyProps;
    productListText?: TypographyProps;
    descriptionText?: TypographyProps;
    categoryNameWrapper?: GridItemProps;
    productListImageWrapper?: GridItemProps;
    productListNameWrapper?: GridItemProps;
    iconWrapper?: GridItemProps;
    image?: InjectableCss;
}

export const categoryNameStyles: CategoryNameStyles = {
    titleText: {
        variant: "h1",
        css: css`
            width: 100%;
            font-weight: 700;
            text-align: center;
            text-transform: uppercase;
            line-height: 4rem;
            margin-bottom: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 45px;
                    `,
                    css`
                        font-size: 45px;
                    `,
                    css`
                        font-size: 60px;
                    `,
                    css`
                        font-size: 60px;
                    `,

                    css`
                        font-size: 60px;
                    `,
                ])}
        `,
    },
    productListText: {
        variant: "h1",
        css: css`
            width: 100%;
            font-weight: 700;
            font-size: 40px;
            border-bottom: 1px solid #cccccc;
        `,
    },
    descriptionText: {
        css: css`
            background: #ffd100;
            text-align: center;
            margin-bottom: 30px;
        `,
    },
    categoryNameWrapper: {
        width: 12,
        css: css`
            justify-content: center;
            min-height: 235px;
            align-items: center;
            flex-direction: column;
        `,
    },
    productListImageWrapper: {
        width: [12, 12, 5, 5, 5],
        css: css`
            align-items: start;
            flex-direction: column;
        `,
    },
    productListNameWrapper: {
        width: [12, 12, 7, 7, 7],
        css: css`
            justify-content: flex-start;
            flex-direction: column;
        `,
    },
    iconWrapper: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: auto;
            height: 100px;

            svg {
                width: 80px;
                height: 100%;
                object-fit: contain;
                margin-bottom: 25px;
                margin-top: 25px;
            }
        `,
    },
    image: {
        css: css`
            border: 1px solid lightgray;
            width: 100%;
            min-height: 400px;
            text-align: center;
        `,
    },
};

const categoryIconMap: Record<string, React.ComponentType> = {
    packangingandshipping: CategoriesIcon,
    safetyandsecurity: SafetyAndSecurityIcon,
    facilityoperations: FacilityOperationsIcon,
    cleaningandjanitorial: CleaningAndJanitorialIcon,
};

const CategoryName = ({ category, currentPage }: Props) => {
    if (!category) {
        return null;
    }

    // Extract parent category from path
    const getParentCategory = (path: string): string => {
        const pathParts = path.split("/Catalog");
        if (pathParts.length > 1) {
            const afterCatalog = pathParts[1].split("/").filter(part => part.length > 0);
            return afterCatalog.length > 0 ? afterCatalog[0].toLowerCase() : "";
        }
        return "";
    };

    const parentCategory = getParentCategory(category.path);

    // Get icon component from mapping, with fallback
    const IconComponent = categoryIconMap[parentCategory] || CategoriesIcon;
    return (
        <>
            {currentPage?.type !== "ProductListPage" && (
                <GridItem {...categoryNameStyles.categoryNameWrapper}>
                    <GridItem {...categoryNameStyles.iconWrapper}>
                        <IconComponent />
                    </GridItem>
                    <Typography
                        {...categoryNameStyles.titleText}
                        variant="h1"
                        data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                    >
                        {category.shortDescription ?? category.name}
                    </Typography>
                </GridItem>
            )}
            {currentPage?.type === "ProductListPage" && (
                <GridContainer>
                    <GridItem {...categoryNameStyles.productListImageWrapper}>
                        <Hidden above="sm" style={{ width: "100%" }}>
                            <Typography
                                {...categoryNameStyles.productListText}
                                variant="h1"
                                data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                            >
                                {category.shortDescription ?? category.name}
                            </Typography>
                        </Hidden>

                        <StyledWrapper {...categoryNameStyles.image}>
                            <LazyImage
                                src={category.smallImagePath === "" ? category.largeImagePath : category.smallImagePath}
                                altText={category.imageAltText}
                                data-test-selector="categoryImagePath"
                            />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem {...categoryNameStyles.productListNameWrapper}>
                        <Hidden below="md" style={{ width: "100%" }}>
                            <Typography
                                {...categoryNameStyles.productListText}
                                variant="h1"
                                data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                            >
                                {category.shortDescription ?? category.name}
                            </Typography>
                        </Hidden>
                        {category.htmlContent && <Typography>{parse(category.htmlContent, parserOptions)}</Typography>}
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(CategoryName)),
    definition: {
        group: "Category Details",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
